import { NavigationFlowHandler } from '@adapters/navigation/navigationFlowHandler';
import { Router } from '@angular/router';
import { Logger } from '@core/model/domain/logger';
import { ParentProcessEvent, ProcessEventType } from '@shared/models/processEvents';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';

export class InitVM {
  constructor(
    private readonly storeService: StoreService,
    private readonly router: Router,
    private readonly logger: Logger,
    private readonly processEventService: ProcessEventService,
    private readonly navigationFlowHandler: NavigationFlowHandler
  ) {}

  async initUserJourney(input: {
    userToken: string;
    userId: string;
    externalConnectionId: string | null;
    tagging: string | null;
    error: string | null;
    errorDescription: string | null;
    isWebauthRedirectionGoingToPartner: string;
  }) {
    const {
      userToken,
      userId,
      tagging,
      externalConnectionId,
      error,
      errorDescription,
      isWebauthRedirectionGoingToPartner,
    } = input;
    this.logUserRestartedJourney(userId);
    this.logInitPageView();
    await this.initStore({
      userId,
      userToken,
      tagging,
      isWebauthRedirectionGoingToPartner,
      error,
    });

    this.handleParentEvent();
    this.handlePageRefresh();
    this.handleBrowserBackAndForwardNavigation();

    if (error) {
      this.handleUserFailedWebauth({ error, errorDescription });
      return;
    }

    if (externalConnectionId) {
      this.handleUserSucceededWebauth({ externalConnectionId });
      return;
    }

    this.handleNewUser();
  }

  private logInitPageView() {
    this.logger.logPageView('INIT');
  }

  private handleUserSucceededWebauth(input: { externalConnectionId: string }) {
    const { externalConnectionId } = input;
    this.storeService.setVariable('JOURNEY_TYPE', {
      type: 'WEBAUTH_SUCCESS',
      payload: { externalConnectionId },
    });
    this.navigateToProcess();
  }

  private handleUserFailedWebauth(input: { error: string; errorDescription: string | null }) {
    const { error, errorDescription } = input;
    this.storeService.setVariable('JOURNEY_TYPE', {
      type: 'WEBAUTH_FAILED',
      payload: { error, errorDescription },
    });
    this.navigateToProcess();
  }

  private handleNewUser() {
    this.storeService.setVariable('JOURNEY_TYPE', { type: 'NEW_USER' });
    this.navigateToProcess();
  }

  private async initStore(input: {
    userId: string;
    userToken: string;
    tagging: string | null;
    isWebauthRedirectionGoingToPartner: string;
    error: string | null;
  }) {
    const { userId, userToken, tagging, isWebauthRedirectionGoingToPartner } = input;

    this.storeService.reset();
    this.storeService.setVariable('USER_ID', userId);
    this.storeService.setVariable('OPEN_BANKING_USER_TOKEN', userToken);
    this.storeService.setVariable('PROCESS_PAGE_HISTORY', []);
    this.storeService.setVariable('SHOULD_IGNORE_NEXT_POPSTATE', false);
    this.storeService.setVariable('IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON', false);

    const isWebauthRedirectionGoingToPartnerBoolean =
      isWebauthRedirectionGoingToPartner === 'true' ? true : false;
    this.storeService.setVariable(
      'IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER',
      isWebauthRedirectionGoingToPartnerBoolean
    );

    if (tagging) {
      this.storeService.setVariable('TAGGING_INIT_DATA_IN_BASE64', tagging);
    }
  }

  private logUserRestartedJourney(queryParamUserId: string) {
    const storedUserId = this.storeService.getVariable('USER_ID');

    if (storedUserId != null && storedUserId != queryParamUserId) {
      this.logger.logEvent('USER_RESTARTED_JOURNEY', {
        storedUserId,
        queryParamUserId,
      });
    }
  }

  /*
    Angular adds a first state which looks like : {navigationId: 1}.
  */
  private navigateToProcess() {
    history.replaceState('INIT', '', '/process');
    this.router.navigate(['process']);
  }

  private handleParentEvent() {
    this.processEventService.subscribe(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.handleEvent.bind(this)
    );
  }

  private handleEvent(event: ParentProcessEvent) {
    if (event.type === ProcessEventType.WINDOW_RESIZE_EVENT) {
      window.parent.postMessage(`OPEN_BANKING_RESIZE_EVENT: ${event.data}`, '*');
    }
  }

  private handlePageRefresh() {
    /*
      beforeunload gets triggered on : 
        - page leave (close tab and tab url redirection) 
        - page refresh
        - window.dispatchEvent  

      There is no way to differentiate them
    */
    window.addEventListener('beforeunload', () => {
      this.logger.logEvent('USER_REFRESHED_OR_LEFT_OPEN_BANKING_JOURNEY');
    });
  }

  private handleBrowserBackAndForwardNavigation() {
    /*
      popstate is triggered when the state object from History API is 
      about to change, this happens when : 
        - history.forward, history.back, history.go 
        - browser back and forward button

      The last back button push before page leave doesn't trigger popstate,
      it's handled directly by beforeunload event
    */
    window.addEventListener('popstate', (event: PopStateEvent) => {
      this.navigationFlowHandler.handlePopstate(event);
    });
  }
}
